import styled from "styled-components";

const navy = "#0c3654";
const red = "#c72b18";
const coral = "#ff6c55";

export const Question = styled.p`
  margin: 0;
  padding-left: 22px;
  border-left: 3px solid ${coral};
  color: var(--project-emphasis);
  font-size: 23px;
  line-height: 34px;
  font-weight: 530;
  text-align: left;
`;

export const Hero = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(64px, 8vw, 112px) max(7vw, 48px);
  overflow: hidden;
  color: #ffffff;
  background:
    radial-gradient(circle at 83% 24%, rgba(255,136,81,.95), transparent 31%),
    radial-gradient(circle at 67% 76%, rgba(44,206,198,.55), transparent 30%),
    linear-gradient(130deg, #072e4b 10%, #0d3e5c 48%, #203142 100%);
`;

export const HeroInner = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, .9fr) minmax(380px, 1.1fr);
  gap: clamp(44px, 7vw, 96px);
  align-items: center;
  max-width: 1120px;
  margin: 0 auto;

  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

export const HeroCopy = styled.div`
  span {
    display: block;
    margin-bottom: 17px;
    color: #89ebd9;
    font-size: 11px;
    line-height: 17px;
    font-weight: 650;
    letter-spacing: .08em;
  }

  h2 {
    margin: 0 0 22px;
    font-size: clamp(43px, 6vw, 76px);
    line-height: .99;
    font-weight: 560;
    letter-spacing: -.05em;
  }

  p {
    max-width: 520px;
    margin: 0;
    color: #c3d4dc;
    font-size: 18px;
    line-height: 29px;
  }
`;

export const HeroLoop = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 13px;
  width: min(100%, 530px);
  margin: 0 auto;

  &::before {
    content: "";
    position: absolute;
    inset: 14% 14%;
    border: 2px solid rgba(255,255,255,.28);
    border-radius: 50%;
    transform: rotate(-13deg);
  }
`;

export const HeroLoopStep = styled.div<{ tone: string }>`
  position: relative;
  z-index: 1;
  min-height: 126px;
  padding: 20px;
  border: 1px solid rgba(255,255,255,.24);
  border-radius: 18px;
  color: #ffffff;
  background: ${(props) => props.tone};
  backdrop-filter: blur(10px);

  span,
  strong { display: block; }

  span {
    margin-bottom: 20px;
    color: rgba(255,255,255,.7);
    font-size: 10px;
    line-height: 16px;
    font-weight: 650;
  }

  strong {
    font-size: clamp(18px, 2.3vw, 27px);
    line-height: 1.08;
    font-weight: 550;
    letter-spacing: -.025em;
  }
`;

export const Process = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(58px, 8vw, 98px) max(7vw, 48px);
  background: #f5f8f9;
`;

export const ProcessInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

export const SectionHeader = styled.div`
  max-width: 760px;
  margin-bottom: 32px;

  span {
    display: block;
    margin-bottom: 9px;
    color: ${red};
    font-size: 10px;
    line-height: 16px;
    font-weight: 650;
    letter-spacing: .08em;
  }

  h3 {
    margin: 0 0 12px;
    color: #162c36;
    font-size: clamp(31px, 4vw, 50px);
    line-height: 1.07;
    font-weight: 540;
    letter-spacing: -.035em;
  }

  p {
    margin: 0;
    color: #60747e;
    font-size: 15px;
    line-height: 24px;
  }
`;

export const ProcessRail = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 850px) { grid-template-columns: 1fr; }
`;

export const ProcessStep = styled.button`
  position: relative;
  min-height: 152px;
  padding: 54px 17px 18px;
  border: 1px solid #d8e3e7;
  border-radius: 13px;
  color: #284b5d;
  background: #ffffff;
  cursor: pointer;
  font: inherit;
  text-align: left;

  &::after {
    content: "→";
    position: absolute;
    top: 50%;
    right: -19px;
    z-index: 2;
    color: #8aabb9;
    transform: translateY(-50%);
  }

  &:last-child::after { display: none; }

  span {
    position: absolute;
    top: 16px;
    left: 16px;
    color: ${red};
    font-size: 10px;
    line-height: 16px;
    font-weight: 650;
  }

  strong,
  small { display: block; }

  strong {
    margin-bottom: 6px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 570;
  }

  small {
    color: #83959e;
    font-size: 11px;
    line-height: 17px;
  }

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    border-color: ${navy};
    outline: none;
    color: #ffffff;
    background: ${navy};

    span { color: #ff8b72; }
    small { color: #b9ced8; }
  }

  @media (max-width: 850px) {
    min-height: 0;
    padding: 18px 18px 18px 62px;

    &::after {
      content: "↓";
      top: auto;
      right: 50%;
      bottom: -18px;
      transform: translateX(50%);
    }
  }
`;

export const ProcessDetail = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, .62fr) minmax(0, 1.38fr);
  gap: 28px;
  margin-top: 18px;
  padding: 28px;
  border: 1px solid #d8e3e7;
  border-radius: 13px;
  background: #ffffff;

  h4 {
    margin: 0;
    color: #173b4c;
    font-size: 22px;
    line-height: 29px;
    font-weight: 550;
  }

  p {
    margin: 0;
    color: #546d79;
    font-size: 14px;
    line-height: 23px;
  }

  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

export const ModelSection = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(58px, 8vw, 100px) max(7vw, 48px);
  color: #ffffff;
  background: #0d3043;
`;

export const ModelInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

export const ModelHeader = styled(SectionHeader)`
  h3 { color: #ffffff; }
  p { color: #b7cad3; }
  span { color: #84e9d5; }
`;

export const ModelTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
`;

export const ModelTab = styled.button`
  padding: 11px 15px;
  border: 1px solid rgba(255,255,255,.18);
  border-radius: 999px;
  color: #9eb6c0;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 12px;

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    outline: none;
    color: #0d3043;
    background: #ffffff;
  }
`;

export const ModelPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(270px, .72fr) minmax(0, 1.28fr);
  gap: clamp(28px, 5vw, 66px);
  align-items: center;
  min-height: 430px;
  padding: clamp(28px, 5vw, 52px);
  border: 1px solid rgba(255,255,255,.13);
  border-radius: 16px;
  background: rgba(0,0,0,.12);

  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

export const ModelCopy = styled.div`
  > span {
    display: block;
    margin-bottom: 10px;
    color: #86ead7;
    font-size: 10px;
    line-height: 16px;
    font-weight: 650;
    letter-spacing: .08em;
  }

  h4 {
    margin: 0 0 14px;
    font-size: clamp(28px, 3.6vw, 43px);
    line-height: 1.08;
    font-weight: 540;
    letter-spacing: -.03em;
  }

  p {
    margin: 0;
    color: #b8cbd3;
    font-size: 15px;
    line-height: 25px;
  }
`;

export const FoggChart = styled.div`
  position: relative;
  min-height: 350px;
  padding: 36px 32px 42px 54px;
  border-left: 2px solid #86aab9;
  border-bottom: 2px solid #86aab9;
  border-radius: 0 0 0 3px;

  &::before {
    content: "Motivation";
    position: absolute;
    top: 44%;
    left: -55px;
    color: #8faab5;
    font-size: 10px;
    letter-spacing: .06em;
    transform: rotate(-90deg);
  }

  &::after {
    content: "Ability: hard to do  →  easy to do";
    position: absolute;
    right: 4px;
    bottom: -29px;
    color: #8faab5;
    font-size: 10px;
    letter-spacing: .04em;
  }
`;

export const FoggCurve = styled.div`
  position: absolute;
  inset: 34px 24px 30px 38px;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -44%;
    left: 5%;
    width: 96%;
    height: 112%;
    border: 3px solid #2d9fcf;
    border-radius: 0 0 62% 0;
    transform: rotate(8deg);
  }
`;

export const FoggPoint = styled.button<{ x: string; y: string; tone: string }>`
  position: absolute;
  z-index: 2;
  top: ${(props) => props.y};
  left: ${(props) => props.x};
  width: 124px;
  min-height: 70px;
  padding: 10px;
  border: 1px solid rgba(255,255,255,.35);
  border-radius: 50%;
  color: #ffffff;
  background: ${(props) => props.tone};
  cursor: pointer;
  font: inherit;
  font-size: 11px;
  line-height: 16px;
  text-align: center;
  transform: translate(-50%, -50%);

  strong,
  span { display: block; }
  strong { font-weight: 630; }
  span { opacity: .75; font-size: 9px; }

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    outline: 3px solid rgba(255,255,255,.3);
    transform: translate(-50%, -50%) scale(1.05);
  }
`;

export const FoggNote = styled.p`
  position: absolute;
  right: 8px;
  bottom: 8px;
  left: 8px;
  z-index: 3;
  margin: 0;
  padding: 11px 13px;
  border-radius: 8px;
  color: #d8e6ec;
  background: rgba(4,25,35,.86);
  font-size: 10px;
  line-height: 16px;
`;

export const HookLoop = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

export const HookStep = styled.button<{ tone: string }>`
  position: relative;
  min-height: 145px;
  padding: 18px;
  border: 1px solid rgba(255,255,255,.18);
  border-radius: 16px;
  color: #ffffff;
  background: ${(props) => props.tone};
  cursor: pointer;
  font: inherit;
  text-align: left;

  span,
  strong,
  small { display: block; }
  span { margin-bottom: 22px; color: rgba(255,255,255,.7); font-size: 10px; }
  strong { margin-bottom: 6px; font-size: 18px; line-height: 23px; font-weight: 570; }
  small { color: rgba(255,255,255,.72); font-size: 10px; line-height: 16px; }

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    outline: 3px solid rgba(255,255,255,.28);
    transform: translateY(-2px);
  }
`;

export const MappingSection = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(58px, 8vw, 98px) max(7vw, 48px);
  background: #fffdfa;
`;

export const MappingInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

export const MappingGrid = styled.div`
  display: grid;
  grid-template-columns: 210px minmax(0, 1fr);
  gap: 14px;

  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

export const MappingTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 720px) {
    flex-direction: row;
    overflow-x: auto;
  }
`;

export const MappingTab = styled.button<{ color: string }>`
  min-height: 88px;
  padding: 18px;
  border: 1px solid #dde5e8;
  border-left: 4px solid ${(props) => props.color};
  border-radius: 9px;
  color: #61747d;
  background: #ffffff;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  line-height: 19px;
  text-align: left;

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    outline: none;
    color: #ffffff;
    background: #173b4c;
  }

  @media (max-width: 720px) { flex: 0 0 170px; }
`;

export const MappingPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(250px, .78fr) minmax(0, 1.22fr);
  gap: clamp(30px, 5vw, 58px);
  align-items: center;
  min-height: 330px;
  padding: clamp(26px, 5vw, 46px);
  border: 1px solid #dde5e8;
  border-radius: 14px;
  background: #ffffff;

  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;

export const MappingCopy = styled.div`
  > span {
    display: block;
    margin-bottom: 9px;
    color: ${red};
    font-size: 10px;
    line-height: 16px;
    font-weight: 650;
    letter-spacing: .07em;
  }

  h4 {
    margin: 0 0 12px;
    color: #183c4c;
    font-size: clamp(25px, 3vw, 36px);
    line-height: 1.08;
    font-weight: 550;
    letter-spacing: -.025em;
  }

  p {
    margin: 0;
    color: #617680;
    font-size: 14px;
    line-height: 23px;
  }
`;

export const MappingFlow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
  align-items: center;

  @media (max-width: 570px) { grid-template-columns: 1fr; }
`;

export const MappingNode = styled.div`
  position: relative;
  min-height: 138px;
  padding: 18px;
  border: 1px solid #dce6ea;
  border-radius: 10px;
  color: #49636f;
  background: #f7fafb;

  &:not(:last-child)::after {
    content: "→";
    position: absolute;
    top: 50%;
    right: -20px;
    color: #829fab;
    transform: translateY(-50%);
  }

  span,
  strong { display: block; }
  span { margin-bottom: 10px; color: #8aa0aa; font-size: 9px; font-weight: 650; }
  strong { font-size: 13px; line-height: 20px; font-weight: 560; }

  @media (max-width: 570px) {
    min-height: 0;
    &:not(:last-child)::after { content: "↓"; top: auto; right: 50%; bottom: -17px; transform: translateX(50%); }
  }
`;

export const EvidenceSection = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(58px, 8vw, 100px) max(7vw, 48px);
  background: #f3f7f9;
`;

export const EvidenceInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
`;

export const EvidenceTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  overflow-x: auto;
`;

export const EvidenceTab = styled.button`
  flex: 0 0 auto;
  padding: 10px 14px;
  border: 1px solid #cfdde3;
  border-radius: 999px;
  color: #6d828c;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 11px;

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] { outline: none; color: #ffffff; background: ${navy}; }
`;

export const EvidenceFigure = styled.figure`
  position: relative;
  margin: 0;
  overflow: hidden;
  border: 1px solid #d4e0e5;
  border-radius: 14px;
  background: #ffffff;

  button {
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: zoom-in;
  }

  img {
    display: block;
    width: 100%;
    height: clamp(360px, 56vw, 720px);
    object-fit: contain;
  }

  figcaption {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 18px;
    border-top: 1px solid #e2e9ec;
    color: #627985;
    font-size: 11px;
    line-height: 17px;
  }

  figcaption strong { color: #274b5b; font-weight: 580; }
`;

export const RelationshipSection = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: clamp(64px, 8vw, 104px) max(7vw, 48px);
  overflow: hidden;
  color: #ffffff;
  background:
    radial-gradient(circle at 90% 10%, rgba(31,167,197,.2), transparent 34%),
    linear-gradient(145deg, #071f2c, #0d3448 68%, #153d4c);
`;

export const RelationshipInner = styled.div`
  max-width: 1080px;
  margin: 0 auto;

  ${SectionHeader} {
    max-width: 720px;
    margin-bottom: 0;

    span { color: #8be8d5; }
    h3 {
      max-width: 680px;
      color: #ffffff;
      font-size: clamp(34px, 4.4vw, 52px);
    }
    p {
      max-width: 600px;
      color: #a9c0ca;
      font-size: 14px;
      line-height: 23px;
    }
  }
`;

export const RelationshipFlow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 18px;
  margin: 34px 0 46px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const RelationshipFlowNode = styled.div`
  position: relative;
  min-height: 82px;
  padding: 16px;
  border: 1px solid rgba(255,255,255,.17);
  border-radius: 13px;
  background: rgba(255,255,255,.055);

  &:not(:last-child)::after {
    content: "→";
    position: absolute;
    top: 50%;
    right: -16px;
    z-index: 2;
    color: #6ab7c6;
    transform: translateY(-50%);
  }

  &:last-child {
    border-color: rgba(133,235,214,.5);
    background: rgba(92,208,184,.12);
  }

  span,
  strong { display: block; }

  span {
    margin-bottom: 14px;
    color: #79c8d4;
    font-size: 9px;
    line-height: 14px;
    font-weight: 650;
  }

  strong {
    color: #eaf4f7;
    font-size: 12px;
    line-height: 18px;
    font-weight: 560;
  }

  @media (max-width: 760px) {
    min-height: 0;
    padding-left: 54px;

    span { position: absolute; top: 17px; left: 17px; margin: 0; }

    &:not(:last-child)::after {
      content: "↓";
      top: auto;
      right: 50%;
      bottom: -17px;
      transform: translateX(50%);
    }
  }
`;

export const RelationshipLayout = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 14px;

  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;

export const RelationshipRail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 820px) {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 4px;
  }
`;

export const RelationshipStep = styled.button`
  position: relative;
  min-height: 82px;
  padding: 15px 15px 15px 54px;
  border: 1px solid rgba(255,255,255,.15);
  border-radius: 11px;
  color: #b9cbd3;
  background: rgba(255,255,255,.035);
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: color .18s ease, background .18s ease, border-color .18s ease;

  > span {
    position: absolute;
    top: 17px;
    left: 16px;
    color: #69bbca;
    font-size: 9px;
    line-height: 14px;
    font-weight: 650;
  }

  strong,
  small { display: block; }

  strong {
    margin-bottom: 4px;
    font-size: 13px;
    line-height: 19px;
    font-weight: 570;
  }

  small {
    color: #6f929f;
    font-size: 9px;
    line-height: 14px;
  }

  &:hover,
  &:focus-visible,
  &[aria-pressed="true"] {
    outline: none;
    border-color: #82dfcf;
    color: #ffffff;
    background: rgba(104,211,191,.14);

    small { color: #9ce7d9; }
  }

  @media (max-width: 820px) { flex: 0 0 190px; }
`;

export const RelationshipPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, .72fr) minmax(330px, 1.28fr);
  gap: clamp(28px, 5vw, 58px);
  align-items: center;
  min-height: 590px;
  padding: clamp(28px, 5vw, 52px);
  border: 1px solid rgba(255,255,255,.15);
  border-radius: 16px;
  background: rgba(0,0,0,.13);

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    min-height: 0;
  }
`;

export const RelationshipCopy = styled.div`
  > span {
    display: block;
    margin-bottom: 13px;
    color: #87e5d3;
    font-size: 9px;
    line-height: 16px;
    font-weight: 650;
    letter-spacing: .055em;
  }

  h4 {
    margin: 0 0 14px;
    color: #ffffff;
    font-size: clamp(29px, 3.3vw, 40px);
    line-height: 1.08;
    font-weight: 540;
    letter-spacing: -.03em;
  }

  p {
    margin: 0;
    color: #b1c6ce;
    font-size: 13px;
    line-height: 22px;
  }

  dl {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1px;
    margin: 20px 0 0;
    overflow: hidden;
    border: 1px solid rgba(255,255,255,.11);
    border-radius: 10px;
    background: rgba(255,255,255,.11);
  }

  dl div {
    padding: 12px 13px;
    background: #0c3141;
  }

  dt {
    margin-bottom: 4px;
    color: #80ddcd;
    font-size: 9px;
    line-height: 14px;
    font-weight: 650;
  }

  dd {
    margin: 0;
    color: #c5d7dd;
    font-size: 10px;
    line-height: 16px;
  }

  blockquote {
    margin: 20px 0 0;
    padding: 2px 0 2px 15px;
    border-left: 2px solid #7dd8c8;
    color: #d7e4e8;
    font-size: 12px;
    line-height: 20px;
  }

  @media (max-width: 520px) {
    dl { grid-template-columns: 1fr; }
  }
`;

export const RelationshipFigure = styled.figure`
  min-width: 0;
  margin: 0;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,.18);
  border-radius: 12px;
  background: #ffffff;

  img {
    display: block;
    width: 100%;
    height: clamp(270px, 32vw, 390px);
    object-fit: contain;
    background: #ffffff;
  }

  figcaption {
    display: flex;
    justify-content: space-between;
    gap: 15px;
    padding: 11px 13px;
    border-top: 1px solid #e1e9ec;
    align-items: center;
    color: #5d747f;
    background: #ffffff;
    font-size: 9px;
    line-height: 15px;
  }

  figcaption > span { max-width: 70%; }
`;

export const ProposalCarouselHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin: 26px auto 0;
  width: calc(100% - 92px);
  max-width: 924px;

  @media (max-width: 600px) {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 10px 14px;
    width: calc(100% - 16px);
  }
`;

export const ProposalSwipeHint = styled.p`
  margin: 0;

  strong,
  span { display: block; }

  strong {
    margin-bottom: 3px;
    color: #ffffff;
    font-size: 12px;
    line-height: 18px;
    font-weight: 580;
  }

  span {
    color: #82a3b0;
    font-size: 10px;
    line-height: 16px;
  }

  @media (max-width: 600px) { span { display: none; } }
`;

export const ProposalFlipHint = styled.button`
  display: none;

  @media (max-width: 600px) {
    display: flex;
    grid-column: 1 / -1;
    align-items: center;
    justify-content: center;
    min-height: 38px;
    padding: 9px 14px;
    border: 1px solid rgba(125,224,206,.32);
    border-radius: 10px;
    color: #bfeee4;
    background: rgba(100,205,186,.08);
    cursor: pointer;
    font: inherit;
    font-size: 10px;
    line-height: 16px;
    font-weight: 570;
    text-align: center;

    &:focus-visible {
      outline: 2px solid rgba(125,224,206,.6);
      outline-offset: 2px;
    }
  }
`;

export const ProposalCarouselActions = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

export const ProposalCarouselButton = styled.button`
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid rgba(255,255,255,.22);
  border-radius: 50%;
  color: #ffffff;
  background: rgba(255,255,255,.07);
  cursor: pointer;
  font: inherit;
  font-size: 16px;
  line-height: 1;
  transition: border-color .18s ease, background .18s ease, opacity .18s ease;

  &:hover,
  &:focus-visible {
    outline: none;
    border-color: #82dfcf;
    background: rgba(104,211,191,.16);
  }

  &:disabled {
    opacity: .3;
    cursor: default;
  }
`;

export const ProposalCarouselCount = styled.span`
  min-width: 48px;
  color: #a7c0ca;
  font-size: 10px;
  line-height: 16px;
  font-variant-numeric: tabular-nums;
  text-align: center;
`;

export const ProposalCarouselTrack = styled.div<{ $dragging: boolean }>`
  position: relative;
  display: grid;
  width: 100%;
  padding: 64px 0 48px;
  overflow: visible;
  touch-action: pan-y;
  user-select: ${(props) => props.$dragging ? "none" : "auto"};

  &:focus-visible {
    outline: 2px solid rgba(130,223,207,.78);
    outline-offset: 5px;
    border-radius: 18px;
  }

  @media (max-width: 600px) { padding: 60px 0 52px; }
`;

export const ProposalCard = styled.article<{
  $accent: string;
  $depth: number;
  $front: boolean;
  $dragging: boolean;
  $offset: number;
  $exitDirection: "left" | "right" | null;
}>`
  position: relative;
  box-sizing: border-box;
  display: block;
  grid-area: 1 / 1;
  z-index: ${(props) => 10 - props.$depth};
  justify-self: center;
  width: calc(100% - 92px);
  max-width: 924px;
  min-height: 500px;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,.16);
  border-radius: 18px;
  background:
    radial-gradient(circle at 102% -5%, ${(props) => props.$accent}28, transparent 34%),
    #092a39;
  box-shadow: 0 22px 46px rgba(0,0,0,.22);
  opacity: ${(props) => props.$exitDirection ? 0 : Math.max(.58, 1 - props.$depth * .09)};
  pointer-events: ${(props) => props.$front ? "auto" : "none"};
  transform: ${(props) => {
    if (props.$exitDirection === "left") {
      return "translateX(calc(-100% - 160px)) translateY(36px) rotate(-8deg)";
    }
    if (props.$exitDirection === "right") {
      return "translateX(calc(100% + 160px)) translateY(36px) rotate(8deg)";
    }
    if (props.$front) {
      return `translateX(${props.$offset}px) translateY(28px) rotate(${props.$offset * .006}deg)`;
    }
    const depth = Math.min(props.$depth, 4);
    const xOffsets = [0, -16, 13, -9, 18];
    const rotations = [0, -1.15, .85, -.6, 1.05];
    return `translateX(${xOffsets[depth]}px) translateY(${28 - depth * 14}px) rotate(${rotations[depth]}deg) scale(${1 - depth * .009})`;
  }};
  transform-origin: center bottom;
  transition: ${(props) => props.$dragging
    ? "none"
    : "transform .42s cubic-bezier(.22,.8,.2,1), opacity .3s ease, filter .3s ease"};
  filter: ${(props) => props.$front ? "none" : `brightness(${1 - props.$depth * .055})`};

  &::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 3px;
    background: ${(props) => props.$accent};
  }

  @media (max-width: 850px) {
    width: calc(100% - 54px);
    height: 670px;
    min-height: 670px;
  }

  @media (max-width: 600px) {
    width: calc(100% - 16px);
    height: 640px;
    min-height: 640px;
    border-radius: 16px;
  }
`;

export const ProposalCardPeek = styled.span<{
  $visible: boolean;
  $accent: string;
}>`
  position: absolute;
  top: 3px;
  left: 22px;
  z-index: 4;
  display: ${(props) => props.$visible ? "block" : "none"};
  max-width: calc(100% - 44px);
  overflow: hidden;
  color: ${(props) => props.$accent};
  font-size: 7px;
  line-height: 10px;
  font-weight: 650;
  letter-spacing: .04em;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
`;

export const ProposalCardInner = styled.div<{ $flipped: boolean }>`
  position: absolute;
  inset: 0;
  transform: ${(props) => props.$flipped ? "rotateY(180deg)" : "rotateY(0)"};
  transform-style: preserve-3d;
  transition: transform .5s cubic-bezier(.25,.75,.2,1);

  @media (prefers-reduced-motion: reduce) { transition-duration: .01ms; }
`;

export const ProposalCardFace = styled.div<{
  $back?: boolean;
  $accent: string;
}>`
  position: absolute;
  inset: 0;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: ${(props) => props.$back
    ? "minmax(0, 1fr)"
    : "minmax(260px, .86fr) minmax(330px, 1.14fr)"};
  gap: clamp(28px, 4.4vw, 46px);
  align-items: center;
  padding: clamp(28px, 4vw, 38px);
  overflow: hidden;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform: ${(props) => props.$back ? "rotateY(180deg)" : "rotateY(0)"};

  ${RelationshipCopy} > span { color: ${(props) => props.$accent}; }

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    align-content: center;
  }

  @media (max-width: 600px) {
    gap: 20px;
    padding: 23px 19px 19px;
    overflow-y: auto;

    ${RelationshipCopy} h4 { font-size: clamp(27px, 8.8vw, 37px); }
    ${RelationshipFigure} img { height: clamp(210px, 64vw, 285px); }
  }
`;

export const ProposalCardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 22px;
`;

export const ProposalCardButton = styled.button<{
  $primary?: boolean;
  $mobileHidden?: boolean;
}>`
  flex: 0 0 auto;
  padding: 9px 13px;
  border: 1px solid ${(props) => props.$primary
    ? "rgba(125,224,206,.7)"
    : "rgba(22,111,145,.25)"};
  border-radius: 999px;
  color: ${(props) => props.$primary ? "#eafffb" : "#176f91"};
  background: ${(props) => props.$primary
    ? "rgba(103,211,191,.13)"
    : "#f3f8fa"};
  cursor: pointer;
  font: inherit;
  font-size: 10px;
  line-height: 14px;
  font-weight: 600;

  &:hover,
  &:focus-visible {
    outline: 2px solid rgba(125,224,206,.55);
    outline-offset: 2px;
  }

  @media (max-width: 600px) {
    display: ${(props) => props.$mobileHidden ? "none" : "inline-flex"};
  }
`;

export const ProposalBackCopy = styled.div<{ $accent: string }>`
  width: min(100%, 760px);
  margin: 0 auto;

  > span {
    display: block;
    margin-bottom: 12px;
    font-size: 9px;
    line-height: 15px;
    font-weight: 650;
    letter-spacing: .055em;
    color: ${(props) => props.$accent};
  }

  h4 {
    max-width: 560px;
    margin: 14px 0 12px;
    color: #ffffff;
    font-size: clamp(28px, 3.2vw, 40px);
    line-height: 1.08;
    font-weight: 540;
    letter-spacing: -.03em;
  }

  > p {
    max-width: 660px;
    margin: 0;
    color: #aec4cc;
    font-size: 13px;
    line-height: 22px;
  }

  dl {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin: 20px 0 0;
  }

  dl div {
    padding: 13px 14px;
    border: 1px solid rgba(255,255,255,.11);
    border-radius: 10px;
    background: rgba(255,255,255,.045);
  }

  dt {
    margin-bottom: 5px;
    font-size: 9px;
    line-height: 14px;
    font-weight: 650;
    color: ${(props) => props.$accent};
  }

  dd {
    margin: 0;
    color: #ccdde2;
    font-size: 11px;
    line-height: 17px;
  }

  blockquote {
    margin: 18px 0 0;
    padding: 2px 0 2px 15px;
    border-left: 2px solid ${(props) => props.$accent};
    color: #dce8eb;
    font-size: 12px;
    line-height: 20px;
  }

  @media (max-width: 520px) {
    dl { grid-template-columns: 1fr; }
    h4 { font-size: clamp(26px, 8.3vw, 35px); }
    > p { font-size: 12px; line-height: 20px; }
    blockquote { font-size: 11px; line-height: 18px; }
  }
`;

export const ProposalPurpose = styled.strong`
  display: inline-flex;
  padding: 6px 10px;
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 999px;
  color: #c5d8de;
  background: rgba(255,255,255,.055);
  font-size: 9px;
  line-height: 13px;
  font-weight: 560;
`;

export const ProposalCarouselFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 22px;

  @media (max-width: 600px) { margin-top: 26px; }
`;

export const ProposalDots = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

export const ProposalDot = styled.button<{
  $active: boolean;
  $accent: string;
}>`
  width: ${(props) => props.$active ? "28px" : "7px"};
  height: 7px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: ${(props) => props.$active ? props.$accent : "rgba(255,255,255,.24)"};
  cursor: pointer;
  transition: width .22s ease, background .22s ease;

  &:hover,
  &:focus-visible {
    outline: 2px solid rgba(255,255,255,.55);
    outline-offset: 3px;
  }
`;

export const Outcome = styled.section`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(220px, .65fr) minmax(0, 1.35fr);
  gap: clamp(30px, 6vw, 90px);
  width: 100%;
  padding: clamp(64px, 8vw, 108px) max(7vw, 48px);
  color: #ffffff;
  background: linear-gradient(130deg, #d7381e, #ed7b46 55%, #169dba 125%);

  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;

export const OutcomeMetric = styled.div`
  span,
  strong { display: block; }
  span { margin-bottom: 12px; color: rgba(255,255,255,.75); font-size: 10px; font-weight: 650; letter-spacing: .08em; }
  strong { font-size: clamp(54px, 8vw, 106px); line-height: .86; font-weight: 560; letter-spacing: -.06em; }
`;

export const OutcomeCopy = styled.div`
  h3 { margin: 0 0 18px; font-size: clamp(30px, 4vw, 48px); line-height: 1.08; font-weight: 540; letter-spacing: -.03em; }
  p { max-width: 680px; margin: 0; color: rgba(255,255,255,.86); font-size: 15px; line-height: 25px; }
`;

export const Lightbox = styled.div`
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 24px 24px;
  background: rgba(5,13,17,.95);

  img { display: block; max-width: 96vw; max-height: calc(100vh - 92px); object-fit: contain; }
`;

export const LightboxClose = styled.button`
  position: absolute;
  top: 18px;
  right: 22px;
  padding: 8px 12px;
  border: 1px solid rgba(255,255,255,.35);
  border-radius: 999px;
  color: #ffffff;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
`;
